import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Only import the default language synchronously
import it from './locales/it.json';
import en from './locales/en.json';

// Initial resources with only IT and EN (most common)
const resources = {
  it: { translation: it },
  en: { translation: en }
};

// Supported languages config with metadata
export const supportedLanguages = [
  { code: 'it', name: 'Italiano', flag: '🇮🇹', nativeName: 'Italiano' },
  { code: 'en', name: 'English', flag: '🇬🇧', nativeName: 'English' },
  { code: 'fr', name: 'French', flag: '🇫🇷', nativeName: 'Français' },
  { code: 'de', name: 'German', flag: '🇩🇪', nativeName: 'Deutsch' },
  { code: 'es', name: 'Spanish', flag: '🇪🇸', nativeName: 'Español' },
  { code: 'pt', name: 'Portuguese', flag: '🇵🇹', nativeName: 'Português' },
  { code: 'ru', name: 'Russian', flag: '🇷🇺', nativeName: 'Русский' },
  { code: 'ja', name: 'Japanese', flag: '🇯🇵', nativeName: '日本語' },
  { code: 'nl', name: 'Dutch', flag: '🇳🇱', nativeName: 'Nederlands' },
  { code: 'zh', name: 'Chinese', flag: '🇨🇳', nativeName: '中文' },
  { code: 'ko', name: 'Korean', flag: '🇰🇷', nativeName: '한국어' },
  { code: 'pl', name: 'Polish', flag: '🇵🇱', nativeName: 'Polski' }
];

export const supportedLanguageCodes = supportedLanguages.map(l => l.code);
export const defaultLanguage = 'it';

// Extract language from URL path
const getLanguageFromPath = () => {
  const path = window.location.pathname;
  const segments = path.split('/').filter(Boolean);
  if (segments.length > 0 && supportedLanguageCodes.includes(segments[0])) {
    return segments[0];
  }
  return null;
};

// Dynamic language loader
const loadLanguage = async (lang) => {
  if (i18n.hasResourceBundle(lang, 'translation')) {
    return;
  }
  
  try {
    let translations;
    switch (lang) {
      case 'fr': translations = await import('./locales/fr.json'); break;
      case 'de': translations = await import('./locales/de.json'); break;
      case 'es': translations = await import('./locales/es.json'); break;
      case 'pt': translations = await import('./locales/pt.json'); break;
      case 'ru': translations = await import('./locales/ru.json'); break;
      case 'ja': translations = await import('./locales/ja.json'); break;
      case 'nl': translations = await import('./locales/nl.json'); break;
      case 'zh': translations = await import('./locales/zh.json'); break;
      case 'ko': translations = await import('./locales/ko.json'); break;
      case 'pl': translations = await import('./locales/pl.json'); break;
      default: return;
    }
    i18n.addResourceBundle(lang, 'translation', translations.default, true, true);
  } catch (e) {
    console.warn(`Failed to load translations for ${lang}`);
  }
};

// Detect and load language from path
const detectedLang = getLanguageFromPath() || defaultLanguage;

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: defaultLanguage,
    lng: detectedLang,
    interpolation: {
      escapeValue: false
    },
    detection: {
      order: ['path', 'localStorage', 'navigator'],
      lookupFromPathIndex: 0,
      caches: ['localStorage']
    }
  });

// Load detected language if not already loaded
if (detectedLang !== 'it' && detectedLang !== 'en') {
  loadLanguage(detectedLang);
}

// Export loader for use in language change
export { loadLanguage };

export default i18n;
